/**
 * Cliente HTTP del SPA.
 *
 * Reglas:
 *  - Siempre envía cookies (credentials: 'include') porque la sesión vive en cookie HttpOnly.
 *  - En métodos state-changing añade el header X-CSRF-Token desde un cache cargado en /api/me.
 *  - Si recibe 401 → redirige a /login (sesión expirada o ausente).
 *  - Nunca lee ni guarda tokens de Entra ID: el BFF se encarga.
 */

import { getCsrfToken } from '@/api/csrf';

const STATE_CHANGING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
    message?: string,
  ) {
    super(message ?? `API error ${status}`);
    this.name = 'ApiError';
  }
}

export interface ApiOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

export async function api<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase();
  const headers = new Headers(options.headers);
  headers.set('Accept', 'application/json');

  let body: BodyInit | undefined;
  if (options.body instanceof FormData) {
    // multipart: el navegador fija el Content-Type con su boundary; no tocarlo.
    body = options.body;
  } else if (options.body !== undefined) {
    headers.set('Content-Type', 'application/json');
    body = JSON.stringify(options.body);
  }

  if (STATE_CHANGING.has(method)) {
    const csrf = await getCsrfToken();
    if (csrf) {
      headers.set('X-CSRF-Token', csrf);
    }
  }

  const res = await fetch(path, {
    ...options,
    method,
    headers,
    body,
    credentials: 'include',
  });

  if (res.status === 401) {
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
      window.location.assign('/login');
    }
    throw new ApiError(401, null, 'Sesión expirada');
  }

  const isJson = res.headers.get('content-type')?.includes('application/json');
  const payload: unknown = isJson ? await res.json() : await res.text();

  if (!res.ok) {
    throw new ApiError(res.status, payload);
  }

  return payload as T;
}
